import ApplicationController from "./application_controller";

const TILE_DEFAULT_CLASSES = ["border-4", "border-transparent"];
const TILE_SELECTED_CLASSES = ["border-4", "border-toggleGreen", "shadow-strong"];

export default class extends ApplicationController {
    connect() {
        super.connect();
        const newPmId = this.element.dataset.paymentMethodNewId;
        if (newPmId) {
            this._autoselectNewCard(newPmId);
            return;
        }
        this._restoreSelection();
    }

    // Runs on connect (initial load and after turbo-stream re-renders the frame).
    // Restores the tile selection when the quote already has a payment method saved —
    // e.g. returning to step 5 after navigating away.
    _restoreSelection() {
        const paymentSelect = document.getElementById("payment-method-input");
        if (paymentSelect?.value) {
            this._preselectTile(paymentSelect.value);
            this._notifyPaymentMethodSelected();
        }
    }

    selectPaymentTile(event) {
        const paymentMethod = event.currentTarget.dataset.paymentMethod;
        this._setPaymentMethod(paymentMethod);
        this._markTileSelected(event.currentTarget);
        this._updateCreditCardTileSubtitle(null);
        this._updateModalCardSelection(null);
        this._notifyPaymentMethodSelected();
    }

    openCardModal() {
        this._preselectDefaultCardIfNeeded();
        this._markTileSelected(document.getElementById("tile-card"));
        document.getElementById("card-modal").classList.remove("hidden");
    }

    _preselectDefaultCardIfNeeded() {
        if (this._hasCardSelected()) return;
        const defaultCardId = document.getElementById("tile-card")?.dataset.defaultCardId;
        if (!defaultCardId) return;
        const label = this._findCardLabel(defaultCardId);
        if (label === null) return;
        this._selectCard(defaultCardId, label);
    }

    _hasCardSelected() {
        const value = document.getElementById("payment-method-input")?.value;
        return value !== "" &&
               document.querySelector(`.card-row[data-payment-method="${value}"]`) !== null;
    }

    closeCardModal(event) {
        const backdrop = document.getElementById("card-modal");
        if (event && event.currentTarget === backdrop && event.target !== backdrop) return; // ignore clicks on modal content, only close on backdrop click
        backdrop.classList.add("hidden");
        this._resetAddCardForm();
        const value = document.getElementById("payment-method-input")?.value;
        if (!value || !document.querySelector(`.card-row[data-payment-method="${value}"]`)) {
            this._unmarkTileSelected(document.getElementById("tile-card"));
            this._updateCreditCardTileSubtitle(null);
            this._updateModalCardSelection(null);
            this._setPaymentMethod("");
            this._notifyPaymentMethodSelected();
        }
    }

    _resetAddCardForm() {
        document.getElementById("add_payment_method").innerHTML = "";
        document.getElementById("add_card_button")?.classList.remove("hidden");
        document.getElementById("card-delete-error")?.classList.add("hidden");
    }

    selectCardFromModal(event) {
        const paymentMethod = event.currentTarget.dataset.paymentMethod;
        const label = event.currentTarget.dataset.label;
        this._selectCard(paymentMethod, label);
        this.closeCardModal();
    }

    removeCardSelection() {
        this._setPaymentMethod("");
        this.closeCardModal();
    }

    async destroyCard(event) {
        event.preventDefault();
        const destroyCardLink = event.currentTarget;

        // prevents user from clicking more than one time
        if (destroyCardLink.dataset.deleting) return;

        const paymentMethod = destroyCardLink.dataset.paymentMethod;
        const errorBanner = document.getElementById("card-delete-error");

        this._setDeleteButtonDisabled(destroyCardLink, true);
        try {
            const response = await fetch(destroyCardLink.href);
            if (!response.ok) {
                this._handleDeleteFailure(destroyCardLink, errorBanner);
                return;
            }
        } catch {
            this._handleDeleteFailure(destroyCardLink, errorBanner);
            return;
        }

        // clear error banner in case a previous attempt left it visible
        errorBanner?.classList.add("hidden");
        destroyCardLink.closest(".card-row").remove();
        if (document.getElementById("payment-method-input")?.value === paymentMethod) {
            // deleted card was selected — clear selection and close modal
            this.removeCardSelection();
        }
    }

    _handleDeleteFailure(link, errorBanner) {
        this._setDeleteButtonDisabled(link, false);
        errorBanner?.classList.remove("hidden");
    }

    _setDeleteButtonDisabled(link, disabled) {
        link.dataset.deleting = disabled ? "true" : "";
        link.style.pointerEvents = disabled ? "none" : "";
        link.classList.toggle("opacity-50", disabled);
    }

    _notifyPaymentMethodSelected() {
        const paymentSelect = document.getElementById("payment-method-input");
        if (!paymentSelect) return;
        const quoteId = paymentSelect.dataset.quoteId;
        if (quoteId) {
            this.stimulate("QuoteFlowReflex#notify_payment_method_selected", quoteId, paymentSelect.value);
        }
    }

    _setPaymentMethod(value) {
        const select = document.getElementById("payment-method-input");
        if (select) select.value = value;
    }

    _markTileSelected(activeTile) {
        document.querySelectorAll(".payment-tile").forEach(tile => {
            tile.classList.remove(...TILE_SELECTED_CLASSES);
            tile.classList.add(...TILE_DEFAULT_CLASSES);
        });
        if (activeTile) {
            activeTile.classList.remove(...TILE_DEFAULT_CLASSES);
            activeTile.classList.add(...TILE_SELECTED_CLASSES);
        }
    }

    _unmarkTileSelected(tile) {
        if (!tile) return;
        tile.classList.remove(...TILE_SELECTED_CLASSES);
        tile.classList.add(...TILE_DEFAULT_CLASSES);
    }

    _updateCreditCardTileSubtitle(selectedCardLabel) {
        const subtitle = document.getElementById("tile-card-subtitle");
        if (!subtitle) return;
        const hasSelection = selectedCardLabel !== null;
        subtitle.textContent = hasSelection ? selectedCardLabel : "No card selected";
        subtitle.classList.toggle("text-gray-500", hasSelection);
        subtitle.classList.toggle("text-amber-600", !hasSelection);
        document.getElementById("tile-card-change")?.classList.toggle("invisible", !hasSelection);
    }

    _updateModalCardSelection(value) {
        const borderClasses = ["border-gray-200", "border-gray-300", "border-toggleGreen"];
        document.querySelectorAll(".card-row").forEach(row => {
            const isSelected = row.dataset.paymentMethod === value;
            row.classList.remove(...borderClasses);
            row.classList.add(isSelected ? "border-toggleGreen" : "border-gray-200");
            row.classList.toggle("bg-gray-50", isSelected);
            row.querySelector(".card-checkmark")?.classList.toggle("hidden", !isSelected);
        });
        const removeBtn = document.getElementById("remove-card-btn");
        if (removeBtn) removeBtn.classList.toggle("hidden", !value);
    }

    // Core card selection: syncs the hidden input, tile styling, subtitle, modal
    // highlight, and reflex. Used by selectCardFromModal and _autoselectNewCard.
    _selectCard(pmId, label) {
        this._setPaymentMethod(pmId);
        this._markTileSelected(document.getElementById("tile-card"));
        this._updateCreditCardTileSubtitle(label ?? null);
        this._updateModalCardSelection(pmId);
        this._notifyPaymentMethodSelected();
    }

    _findCardLabel(pmId) {
        return document.querySelector(`[data-payment-method="${pmId}"][data-label]`)?.dataset.label ?? null;
    }

    _autoselectNewCard(pmId) {
        this._selectCard(pmId, this._findCardLabel(pmId));
    }

    _preselectTile(value) {
        const tile = document.querySelector(`.payment-tile[data-payment-method="${value}"]`)
                     ?? (value ? document.getElementById("tile-card") : null);
        if (!tile) return;
        this._markTileSelected(tile);
        if (tile.id === "tile-card") {
            this._updateCreditCardTileSubtitle(this._findCardLabel(value));
            this._updateModalCardSelection(value);
        }
    }
}
